import { queryOptions, useQuery } from '@tanstack/react-query';

import { TimeRange, QueryOptions } from '@cast/shared-types';
import {
  DboCacheGroup,
  DboCacheGroupWithEndpointMetadata,
  DboEndpointWithMetadata,
} from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useRoxy } from 'features/db-optimization/context/RoxyContext';

const apiFn = apiClient.dbo.getCacheGroup;

export type CacheGroupQueryArgs<SELECT> = QueryOptions<
  DboCacheGroupWithEndpointMetadata,
  SELECT
> & {
  cacheGroupId?: string;
  key?: string;
  getRange?: () => TimeRange;
  roxy?: boolean;
};

const transformEndpoints = (
  endpoints: DboCacheGroup['endpoints']
): DboEndpointWithMetadata[] | undefined => {
  return endpoints?.map((endpoint) => {
    const withMetadata = endpoint as DboEndpointWithMetadata;
    withMetadata.metadata = {
      name: endpoint.suffix || 'default',
    };
    return withMetadata;
  });
};

const DBO_CACHE_GROUP_QUERY_KEY = 'dbo-cache-group';

export const getCacheGroupQueryKey = ({ id }: { id?: string }) =>
  [DBO_CACHE_GROUP_QUERY_KEY, id] as const;

const getCacheGroupQueryOptions = ({
  cacheGroupId,
  getRange,
  enabled,
  roxy,
}: {
  cacheGroupId?: string;
  getRange?: () => TimeRange;
  enabled?: boolean;
  roxy?: boolean;
}) =>
  queryOptions({
    queryKey: getCacheGroupQueryKey({ id: cacheGroupId }),
    queryFn: async (): Promise<DboCacheGroupWithEndpointMetadata> => {
      const { from: metricsRangeStart, to: metricsRangeEnd } =
        getRange?.() || {};
      const { data } = await apiFn({
        id: cacheGroupId!,
        metricsRangeStart,
        metricsRangeEnd,
        roxy,
      });
      return {
        ...data,
        endpoints: transformEndpoints(data.endpoints),
      };
    },
    enabled: makeIsEnabled(!!cacheGroupId, enabled),
  });

export const useCacheGroupQuery = <SELECT = DboCacheGroupWithEndpointMetadata>({
  cacheGroupId,
  getRange,
  enabled,
  select,
  refetchInterval,
  roxy,
}: CacheGroupQueryArgs<SELECT> = {}) => {
  const urlRoxy = useRoxy();
  const effectiveRoxy = roxy ?? urlRoxy;

  return useQuery({
    ...getCacheGroupQueryOptions({
      cacheGroupId,
      getRange,
      enabled,
      roxy: effectiveRoxy,
    }),
    select,
    refetchInterval,
  });
};
